import { Building2, CheckCircle, AlertCircle, Calendar } from 'lucide-react';

interface ReportHeaderProps {
  organization: {
    name: string;
    address: string;
    registrationNumber: string;
    taxId: string;
    idnat?: string;
    creditScore?: number;
    esgScore?: string;
    financialRating?: string;
    logo?: string;
  };
  title: string;
  isAudited: boolean;
  generatedBy: string;
  currency: 'USD' | 'CDF';
  fiscalYear?: string;
  generatedDate?: string;
  period?: {
    startDate?: string;
    endDate?: string;
  };
}

export function ReportHeader({
  organization,
  title,
  isAudited,
  generatedBy,
  currency,
  fiscalYear,
  generatedDate,
  period
}: ReportHeaderProps) {
  const displayPeriod = (() => {
    if (fiscalYear) return `Exercice clos le 31 décembre ${fiscalYear}`;
    if (period?.startDate && period?.endDate) {
      const start = new Date(period.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      const end = new Date(period.endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      return `Période du ${start} au ${end}`;
    }
    return `Exercice clos le 31 décembre ${new Date().getFullYear()}`;
  })();

  const displayGeneratedDate = (() => {
    if (generatedDate) {
      const date = new Date(generatedDate);
      return `${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    const now = new Date();
    return `${now.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} à ${now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  })();

  const currencyLabel = currency === 'USD' ? 'Dollars américains (USD)' : 'Francs congolais (CDF)';

  return (
    <div className="border-b-2 border-primary/20 pb-6 mb-6">
      {/* Bloc entreprise — centré, professionnel */}
      <div className="text-center space-y-2 mb-6">
        <div className="flex items-center justify-center gap-3 mb-1">
          {organization.logo ? (
            <img
              src={organization.logo}
              alt={`${organization.name}`}
              className="h-12 w-auto object-contain"
              style={{ maxWidth: '60px' }}
            />
          ) : (
            <Building2 className="h-10 w-10 text-primary" />
          )}
        </div>
        <h2 className="text-xl font-bold tracking-wide uppercase">{organization.name}</h2>
        {organization.address && (
          <p className="text-sm text-muted-foreground">{organization.address}</p>
        )}
        <p className="text-xs text-muted-foreground">
          {organization.registrationNumber && `RCCM : ${organization.registrationNumber}`}
          {organization.taxId && ` — NIF : ${organization.taxId}`}
          {organization.idnat && ` — IDNAT : ${organization.idnat}`}
        </p>
      </div>

      {/* Titre du document — grand et centré */}
      <div className="text-center mb-5 py-3 border-y border-border">
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1">{displayPeriod}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Monnaie de présentation : {currencyLabel}
        </p>
        <p className="text-xs text-muted-foreground italic">
          Norme comptable : SYSCOHADA — Acte Uniforme OHADA
        </p>
      </div>

      {/* Métadonnées — grille compacte */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>Établi par : <span className="font-medium text-foreground">{generatedBy}</span></span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {displayGeneratedDate}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {isAudited ? (
            <>
              <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
              <span className="font-medium text-emerald-700 dark:text-emerald-400">État audité</span>
            </>
          ) : (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
              <span className="font-medium text-amber-600 dark:text-amber-400">État provisoire — non audité</span>
            </>
          )}
        </div>
      </div>

      {/* Scores — discrets, en ligne secondaire */}
      {(organization.creditScore != null || organization.esgScore || organization.financialRating) && (
        <div className="flex items-center justify-end gap-4 mt-2 text-[10px] text-muted-foreground/70">
          {organization.creditScore != null && (
            <span>Cote crédit : {organization.creditScore}/100</span>
          )}
          {organization.esgScore && (
            <span>ESG : {organization.esgScore}</span>
          )}
          {organization.financialRating && (
            <span>Note financière : {organization.financialRating}</span>
          )}
        </div>
      )}
    </div>
  );
}